import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api from '../../api';

// --- Helper functions outside component to prevent re-renders ---
function getStaffId(staffMember) {
  return staffMember._id || staffMember.id || staffMember.userId || staffMember.authUserId;
}
function getStaffName(staffMember) {
  if (staffMember.userDetails) {
    return `${staffMember.userDetails.firstName || ''} ${staffMember.userDetails.lastName || ''}`.trim();
  }
  return `${staffMember.firstName || ''} ${staffMember.lastName || ''}`.trim() || staffMember.email?.split('@')[0] || 'Unknown';
}
function getStaffEmail(staffMember) {
  return staffMember.email || staffMember.userDetails?.email || '';
}

// --- Icon System ---
const Icons = {
  Search: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  CheckCircle: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  UserAdd: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
    </svg>
  ),
  UserRemove: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
    </svg>
  ),
  Users: () => (
    <svg className="w-5 h-5 sm:w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
    </svg>
  ),
  Filter: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>
  ),
  X: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Refresh: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  )
};

const FacultyAssignmentPanel = ({ 
  tenantId, 
  token, 
  department = {}, 
  onAssign = null, 
  onAssignedChange = null 
}) => {
  const effectiveTenantId = tenantId || department?.tenantId || department?.tenant || '';
  const departmentId = department?._id || department?.id || '';
  
  // State
  const [staff, setStaff] = useState([]);
  const [assignedIds, setAssignedIds] = useState(new Set(department.facultyIds ?? []));
  const [loading, setLoading] = useState(false);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('all');
  
  // Refs to avoid cyclic dependencies
  const isMounted = useRef(true);
  const staffFetchTimeout = useRef(null);
  const assignedFetchTimeout = useRef(null);
  const lastFetchTime = useRef({ staff: 0, assigned: 0 });
  const abortControllers = useRef({ staff: null, assigned: null });

  const cleanup = useCallback(() => {
    if (staffFetchTimeout.current) clearTimeout(staffFetchTimeout.current);
    if (assignedFetchTimeout.current) clearTimeout(assignedFetchTimeout.current);
    
    if (abortControllers.current.staff) {
      abortControllers.current.staff.abort();
      abortControllers.current.staff = null;
    }
    if (abortControllers.current.assigned) {
      abortControllers.current.assigned.abort();
      abortControllers.current.assigned = null;
    }
  }, []);

  const fetchStaff = useCallback(async (force = false, allowAbort = false) => {
    if (!effectiveTenantId || !isMounted.current) return;
    const now = Date.now();
    if (!force && (now - lastFetchTime.current.staff) < 2000) return;

    let signal = undefined;
    if (allowAbort) {
      if (staffFetchTimeout.current) clearTimeout(staffFetchTimeout.current);
      if (abortControllers.current.staff) abortControllers.current.staff.abort();
      abortControllers.current.staff = new AbortController();
      signal = abortControllers.current.staff.signal;
    }
    setLoading(true);
    setError(null);
    try {
      lastFetchTime.current.staff = now;
      const res = await api.get(`/api/admin/staff/${effectiveTenantId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal,
        params: { role: 'faculty', status: 'all', limit: 1000 }
      });
      if (!isMounted.current || (allowAbort && abortControllers.current.staff.signal.aborted)) return;
      let staffData = [];
      if (res?.data?.staff && Array.isArray(res.data.staff)) {
        staffData = res.data.staff;
      } else if (res?.data && Array.isArray(res.data)) {
        staffData = res.data;
      }
      const transformedStaff = staffData.map(staffMember => {
        if (staffMember.userDetails) {
          return {
            ...staffMember,
            id: getStaffId(staffMember),
            firstName: staffMember.userDetails.firstName || '',
            lastName: staffMember.userDetails.lastName || '',
            email: getStaffEmail(staffMember),
            role: staffMember.tenantRole || 'faculty',
            status: staffMember.status,
            authStatus: staffMember.status
          };
        } else {
          return {
            ...staffMember,
            id: getStaffId(staffMember),
            firstName: staffMember.firstName || '',
            lastName: staffMember.lastName || '',
            email: staffMember.email || '',
            role: staffMember.role || 'faculty',
            status: staffMember.status || staffMember.authStatus || 'active'
          };
        }
      });
      const facultyOnly = transformedStaff.filter(s => 
        s.role === 'faculty' || s.role === 'teacher' || s.role === 'instructor'
      );
      if (isMounted.current) setStaff(facultyOnly);
    } catch (err) {
      if (allowAbort && (err.name === 'AbortError' || err.code === 'ERR_CANCELED')) return;
      if (isMounted.current) {
        setError(err?.response?.data?.message || 'Failed to load faculty directory.');
      }
    } finally {
      if (isMounted.current && !(allowAbort && abortControllers.current.staff?.signal.aborted)) {
        setLoading(false);
      }
    }
  }, [effectiveTenantId, token]);

  const fetchAssignedFaculty = useCallback(async (force = false, allowAbort = false) => {
    if (!effectiveTenantId || !departmentId || !isMounted.current) return;
    const now = Date.now();
    if (!force && (now - lastFetchTime.current.assigned) < 2000) return;

    let signal = undefined;
    if (allowAbort) {
      if (assignedFetchTimeout.current) clearTimeout(assignedFetchTimeout.current);
      if (abortControllers.current.assigned) abortControllers.current.assigned.abort();
      abortControllers.current.assigned = new AbortController();
      signal = abortControllers.current.assigned.signal;
    }
    setLoadingAssigned(true);
    try {
      lastFetchTime.current.assigned = now;
      const res = await api.get(`/api/departments/${effectiveTenantId}/${departmentId}/faculty`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal,
      });
      if (!isMounted.current || (allowAbort && abortControllers.current.assigned.signal.aborted)) return;
      const faculty = Array.isArray(res?.data?.faculty) ? res.data.faculty : [];
      const ids = faculty.map(f => getStaffId(f)).filter(Boolean);
      const newAssignedSet = new Set(ids);
      setAssignedIds(newAssignedSet);
      if (onAssignedChange) onAssignedChange(ids);
    } catch (err) {
      if (allowAbort && (err.name === 'AbortError' || err.code === 'ERR_CANCELED')) return;
      if (isMounted.current) setAssignedIds(new Set(department.facultyIds ?? []));
    } finally {
      if (isMounted.current && !(allowAbort && abortControllers.current.assigned?.signal.aborted)) {
        setLoadingAssigned(false);
      }
    }
  }, [effectiveTenantId, departmentId, token, department.facultyIds, onAssignedChange]);

  const hasFetched = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    if (!hasFetched.current) {
      if (effectiveTenantId) fetchStaff(true, false);
      if (effectiveTenantId && departmentId) fetchAssignedFaculty(true, false);
      hasFetched.current = true;
    }
    return () => {
      isMounted.current = false;
      cleanup();
    };
  }, [effectiveTenantId, departmentId, fetchStaff, fetchAssignedFaculty, cleanup]);

  const facultyList = useMemo(() => {
    return staff.filter(s => {
      const role = (s.role || '').toLowerCase();
      return role === 'faculty' || role === 'teacher' || role === 'instructor';
    });
  }, [staff]);

  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    let filteredList = facultyList;
    if (viewMode === 'assigned') {
      filteredList = facultyList.filter(f => assignedIds.has(getStaffId(f)));
    } else if (viewMode === 'unassigned') {
      filteredList = facultyList.filter(f => !assignedIds.has(getStaffId(f)));
    }
    if (q) {
      filteredList = filteredList.filter(f => {
        const name = getStaffName(f).toLowerCase();
        const email = getStaffEmail(f).toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    return filteredList;
  }, [facultyList, query, viewMode, assignedIds]);

  const stats = useMemo(() => ({
    total: facultyList.length,
    assigned: assignedIds.size,
    unassigned: facultyList.length - assignedIds.size,
  }), [facultyList.length, assignedIds.size]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      const allIds = filtered.map(f => getStaffId(f)).filter(Boolean);
      setSelectedIds(new Set(allIds));
    }
  };

  const handleUpdate = async (idsToUpdate, action) => {
    if (!departmentId) {
      setError('Department context missing.');
      return;
    }
    if (idsToUpdate.size === 0) {
      setError('No faculty members selected.');
      return;
    }
    setAssigning(true);
    setError(null);
    const idArray = Array.from(idsToUpdate);
    
    try {
      const newAssigned = new Set(assignedIds);
      idArray.forEach(id => {
        if (action === 'assign') newAssigned.add(id);
        else newAssigned.delete(id);
      });
      setAssignedIds(newAssigned);

      if (action === 'assign') {
        await api.put(
          `/api/assign-faculty/${departmentId}`,
          { tenantId: effectiveTenantId, facultyIds: idArray },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
      } else {
        const unassignPromises = idArray.map(fid => 
          api.put(
            `/api/remove-faculty/${departmentId}`,
            { tenantId: effectiveTenantId, facultyId: fid },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
        );
        await Promise.all(unassignPromises);
      }

      const finalArray = Array.from(newAssigned);
      if (onAssign) onAssign(finalArray);
      if (onAssignedChange) onAssignedChange(finalArray);
      setSelectedIds(new Set());
      fetchAssignedFaculty(true, true);
    } catch (err) {
      setError(`Failed to ${action} faculty. ${err?.response?.data?.message || 'Please try again.'}`);
      fetchAssignedFaculty(true, true);
    } finally {
      setAssigning(false);
    }
  };

  const handleRefresh = () => {
    fetchStaff(true, true);
    fetchAssignedFaculty(true, true);
  };

  const getInitials = (f) => {
    const name = getStaffName(f);
    if (name && name !== 'Unknown') {
      const parts = name.split(' ');
      if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      return name.charAt(0).toUpperCase();
    }
    return getStaffEmail(f).charAt(0).toUpperCase();
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      
      {/* 1. Roster Management Header */}
      <div className="bg-gray-50 p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-base sm:text-xl font-bold text-gray-800 flex items-center gap-2">
              <Icons.Users />
              Faculty Roster
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
              Managing staffing for <span className="font-semibold text-gray-700">{department?.name || 'Department'}</span>
            </p>
          </div>
          
          {/* Statistics View */}
          <div className="flex items-center gap-2 sm:gap-3 self-end sm:self-auto">
            <div className="px-3 py-1.5 bg-white rounded-lg border border-gray-200 shadow-xs text-center min-w-[75px] sm:min-w-[90px]">
              <div className="text-base sm:text-xl font-bold text-gray-800">{stats.assigned}</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-400 font-bold">Assigned</div>
            </div>
            <div className="px-3 py-1.5 bg-white rounded-lg border border-gray-200 shadow-xs text-center min-w-[75px] sm:min-w-[90px]">
              <div className="text-base sm:text-xl font-bold text-gray-400">{stats.unassigned}</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-gray-400 font-bold">In Pool</div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading || loadingAssigned}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-50 border border-gray-200 sm:border-transparent bg-white sm:bg-transparent"
              title="Refresh lists"
            >
              <Icons.Refresh />
            </button>
          </div>
        </div>
      </div>

      {/* 2. Controls Toolbar Options */}
      <div className="p-4 border-b border-gray-100 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4 sticky top-0 z-10">
        
        {/* Navigation Tabs - Horizontal Scroll on Small Screen */}
        <div className="flex p-1 bg-gray-100 rounded-xl overflow-x-auto whitespace-nowrap max-w-full no-scrollbar">
          {[
            { id: 'all', label: 'All Faculty' },
            { id: 'assigned', label: 'Assigned' },
            { id: 'unassigned', label: 'Available Pool' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setViewMode(tab.id); setSelectedIds(new Set()); }}
              className={`px-4 py-2 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                viewMode === tab.id ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Input Search Form field */}
        <div className="relative w-full md:w-72">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            <Icons.Search />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-10 py-3 sm:py-2 border border-gray-300 rounded-lg text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            placeholder="Search name or email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button 
              onClick={() => setQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <Icons.X />
            </button>
          )}
        </div>
      </div>

      {/* 3. Main Roster Component List View */}
      <div className="flex-1 overflow-y-auto min-h-[350px] relative bg-gray-50/40">
        
        {error && (
          <div className="m-4 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100 text-sm flex items-start justify-between gap-2">
            <span className="flex-1 break-words">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 p-0.5">
              <Icons.X />
            </button>
          </div>
        )}

        {loading || loadingAssigned ? (
          <div className="p-4 space-y-2.5">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex items-center p-4 bg-white rounded-xl border border-gray-100 shadow-xs animate-pulse">
                <div className="w-10 h-10 bg-gray-200 rounded-full mr-3"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center text-gray-400">
            <div className="p-3 bg-gray-100 rounded-full mb-2">
              <Icons.Filter />
            </div>
            <p className="text-sm font-semibold text-gray-700">No faculty members found</p>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
              {viewMode === 'assigned' 
                ? 'No faculty members assigned to this department yet.' 
                : viewMode === 'unassigned'
                ? 'All faculty members are already assigned.'
                : 'Try adjusting your search query or view filters.'}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-2 pb-24">
            
            {/* Desktop Table Header Labels - Hidden on Mobile view */}
            <div className="hidden md:flex items-center px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <div className="w-8 flex justify-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onChange={selectAll}
                  disabled={assigning}
                />
              </div>
              <div className="flex-1 ml-4">Faculty Details</div>
              <div className="w-32 text-center">Status</div>
              <div className="w-24 text-right">Action</div>
            </div>

            {/* Select All Tool Strip on Mobile view */}
            <div className="md:hidden flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl mb-3 text-xs font-semibold text-gray-600">
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="mobile-select-all"
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-5 w-5"
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onChange={selectAll}
                  disabled={assigning}
                />
                <label htmlFor="mobile-select-all" className="cursor-pointer">Select All Members</label>
              </span>
              <span className="text-gray-400">({filtered.length} visible)</span>
            </div>

            {/* List Loop Block */}
            {filtered.map(f => {
              const id = getStaffId(f);
              const isAssigned = assignedIds.has(id);
              const isSelected = selectedIds.has(id);
              
              return (
                <label 
                  key={id} 
                  className={`flex flex-col md:flex-row md:items-center p-4 md:p-3 rounded-xl border transition gap-3 select-none cursor-pointer ${
                    isSelected ? 'bg-blue-50/60 border-blue-200 shadow-xs' : 'bg-white border-gray-200 hover:border-blue-100'
                  }`}
                >
                  <div className="flex items-center flex-1 min-w-0 justify-between md:justify-start">
                    <div className="flex items-center min-w-0">
                      {/* Selection Box */}
                      <div className="w-8 flex items-center justify-start flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={assigning}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5 sm:w-4 sm:h-4"
                        />
                      </div>

                      {/* Display Profile Avatar & Labels */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2 ml-1 flex-shrink-0 ${
                        isAssigned ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}>
                        {getInitials(f)}
                      </div>
                      <div className="ml-3 min-w-0 pr-2">
                        <p className={`text-sm font-semibold truncate ${isAssigned ? 'text-blue-900' : 'text-gray-900'}`}>
                          {getStaffName(f)}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{getStaffEmail(f)}</p>
                      </div>
                    </div>

                    {/* Badge Container Block (Mobile Alignment changes) */}
                    <div className="md:hidden">
                      {isAssigned ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-100">
                          Assigned
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                          Available
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop Only Status Output Display */}
                  <div className="hidden md:block w-32 text-center">
                    {isAssigned ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
                        <span className="w-1.5 h-1.5 mr-1.5 bg-green-500 rounded-full"></span>
                        Assigned
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                        Available
                      </span>
                    )}
                  </div>

                  {/* Direct Mutation Trigger Actions Panel */}
                  <div className="border-t border-gray-100 pt-3 md:pt-0 md:border-none flex items-center justify-end md:w-24">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUpdate(new Set([id]), isAssigned ? 'unassign' : 'assign');
                      }}
                      disabled={assigning}
                      className={`w-full md:w-auto px-4 py-2.5 md:p-2 rounded-lg text-xs md:text-sm font-semibold transition inline-flex items-center justify-center gap-1.5 ${
                        isAssigned
                          ? 'text-red-600 bg-red-50 hover:bg-red-100 md:bg-transparent md:text-gray-400 md:hover:text-red-600 md:hover:bg-red-50'
                          : 'text-blue-600 bg-blue-50 hover:bg-blue-100 md:bg-transparent md:text-blue-600 md:hover:bg-blue-50'
                      }`}
                      title={isAssigned ? "Remove from department" : "Assign to department"}
                    >
                      <span className="md:hidden">{isAssigned ? 'Remove From Dept' : 'Assign To Dept'}</span>
                      {isAssigned ? <Icons.UserRemove /> : <Icons.UserAdd />}
                    </button>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. Fixed Footprint Sticky Bulk Processing Controls Drawer */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-xl px-4 py-4 pb-safe transform transition-transform duration-300 ease-in-out z-20 ${
        selectedIds.size > 0 ? 'translate-y-0' : 'translate-y-full'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between max-w-4xl mx-auto gap-4">
          <div className="flex items-center gap-3 justify-center sm:justify-start">
            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">
              {selectedIds.size}
            </div>
            <span className="text-sm text-gray-700 font-semibold">Faculty members selected</span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="flex-1 sm:flex-none px-4 py-3 sm:py-2 text-sm font-semibold text-gray-600 hover:text-gray-900 bg-gray-50 rounded-lg transition"
              disabled={assigning}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleUpdate(selectedIds, 'unassign')}
              disabled={assigning || selectedIds.size === 0}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-100 transition disabled:opacity-50"
            >
              <Icons.UserRemove />
              Unassign
            </button>
            <button
              type="button"
              onClick={() => handleUpdate(selectedIds, 'assign')}
              disabled={assigning || selectedIds.size === 0}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition disabled:opacity-50"
            >
              <Icons.UserAdd />
              Assign
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default FacultyAssignmentPanel;